/**
 * Push Notification Service
 *
 * Handles push notification subscriptions and device registration.
 * Uses the Web Push API and service workers.
 */

import { apiClient } from '@/utils/api';

// -------------------------------------------------------------------
// Types
// -------------------------------------------------------------------

export interface PushDevice {
  deviceId: string;
  browser?: string;
  platform?: string;
  deviceName?: string;
  createdAt: string;
  lastUsedAt?: string;
  endpointPreview?: string;
}

export interface PushDevicesResponse {
  data: PushDevice[];
  meta: {
    total: number;
    pushEnabled: boolean;
  };
}

export interface RegisterDeviceResponse {
  data: {
    deviceId: string;
    browser?: string;
    platform?: string;
    deviceName?: string;
    createdAt: string;
  };
}

export interface VapidKeyResponse {
  data: {
    vapidPublicKey: string;
  };
}

export interface DeleteDeviceResponse {
  data: {
    deleted: boolean;
    deviceId: string;
  };
}

export type PushPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

// -------------------------------------------------------------------
// Helper Functions
// -------------------------------------------------------------------

/**
 * Check if the browser supports push notifications
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Get the current notification permission state
 */
export function getPermissionState(): PushPermissionState {
  if (!isPushSupported()) {
    return 'unsupported';
  }
  return Notification.permission as PushPermissionState;
}

/**
 * Detect browser information for device registration
 */
function detectBrowser(): { browser: string; platform: string } {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let platform = 'Unknown';

  // Detect browser
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browser = 'Chrome';
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox';
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browser = 'Safari';
  } else if (ua.includes('Edg')) {
    browser = 'Edge';
  } else if (ua.includes('Opera') || ua.includes('OPR')) {
    browser = 'Opera';
  }

  // Detect platform
  if (ua.includes('Windows')) {
    platform = 'Windows';
  } else if (ua.includes('Mac OS')) {
    platform = 'macOS';
  } else if (ua.includes('Linux') && !ua.includes('Android')) {
    platform = 'Linux';
  } else if (ua.includes('Android')) {
    platform = 'Android';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    platform = 'iOS';
  }

  return { browser, platform };
}

/**
 * Convert a base64 string to a Uint8Array (for VAPID key)
 */
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray.buffer;
}

// -------------------------------------------------------------------
// Service Worker Management
// -------------------------------------------------------------------

let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

/**
 * Register the service worker (idempotent - only registers once)
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Push] Service workers not supported');
    return null;
  }

  // Return existing registration if available
  if (serviceWorkerRegistration) {
    return serviceWorkerRegistration;
  }

  // Return in-progress registration if one is happening
  if (registrationPromise) {
    return registrationPromise;
  }

  // Start new registration
  registrationPromise = (async () => {
    try {
      console.log('[Push] Registering service worker...');
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      console.log('[Push] Service Worker registered:', registration.scope);
      serviceWorkerRegistration = registration;

      // Wait for the service worker to be ready
      const ready = await navigator.serviceWorker.ready;
      console.log('[Push] Service Worker ready:', ready.scope);

      return registration;
    } catch (error) {
      console.error('[Push] Service Worker registration failed:', error);
      registrationPromise = null; // Allow retry on failure
      return null;
    }
  })();

  return registrationPromise;
}

/**
 * Get the current service worker registration
 */
export async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (serviceWorkerRegistration) {
    return serviceWorkerRegistration;
  }

  if (!('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    serviceWorkerRegistration = registration;
    return registration;
  } catch {
    return null;
  }
}

// -------------------------------------------------------------------
// Push Subscription Management
// -------------------------------------------------------------------

/**
 * Get the VAPID public key from the server
 */
async function getVapidPublicKey(): Promise<string | null> {
  try {
    const response = await apiClient.get<VapidKeyResponse>(
      '/profile/push-devices/vapid-public-key'
    );
    return response.data.vapidPublicKey || null;
  } catch (error) {
    console.error('Failed to get VAPID public key:', error);
    return null;
  }
}

/**
 * Get the current push subscription
 */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    return null;
  }

  try {
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Check if the current device is subscribed to push notifications
 */
export async function isSubscribed(): Promise<boolean> {
  const subscription = await getCurrentSubscription();
  return subscription !== null;
}

/**
 * Request notification permission and subscribe to push notifications
 */
export async function subscribeToPush(): Promise<PushSubscription | null> {
  console.log('[Push] Starting subscription flow...');
  
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported in this browser');
  }

  // Request notification permission
  console.log('[Push] Requesting permission...');
  const permission = await Notification.requestPermission();
  console.log('[Push] Permission result:', permission);
  
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }

  // Get the service worker registration
  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    throw new Error('Service worker not available');
  }
  console.log('[Push] Got service worker registration');

  // Get VAPID public key
  console.log('[Push] Fetching VAPID public key...');
  const vapidPublicKey = await getVapidPublicKey();
  if (!vapidPublicKey) {
    throw new Error('Push notifications not configured on server');
  }
  console.log('[Push] Got VAPID key:', vapidPublicKey.substring(0, 20) + '...');

  try {
    // Subscribe to push notifications
    console.log('[Push] Subscribing to push manager...');
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    console.log('[Push] Subscription created:', subscription.endpoint);
    return subscription;
  } catch (error) {
    console.error('[Push] Failed to subscribe to push:', error);
    throw error;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  const subscription = await getCurrentSubscription();
  if (!subscription) {
    return true;
  }

  try {
    await subscription.unsubscribe();
    console.log('Push subscription cancelled');
    return true;
  } catch (error) {
    console.error('Failed to unsubscribe from push:', error);
    return false;
  }
}

// -------------------------------------------------------------------
// Device Registration API
// -------------------------------------------------------------------

/**
 * Register the current device for push notifications
 */
export async function registerDevice(
  subscription: PushSubscription,
  deviceName?: string
): Promise<RegisterDeviceResponse['data']> {
  const { browser, platform } = detectBrowser();
  console.log('[Push] Registering device:', { browser, platform });

  // Extract the keys from the subscription
  const keys = subscription.toJSON().keys;
  if (!keys?.p256dh || !keys?.auth) {
    throw new Error('Invalid subscription keys');
  }

  console.log('[Push] Sending registration to backend...');
  const response = await apiClient.authPost<RegisterDeviceResponse>(
    '/profile/push-devices',
    {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: keys.p256dh,
        auth: keys.auth,
      },
      browser,
      platform,
      deviceName,
    }
  );

  console.log('[Push] Device registered:', response.data.deviceId);
  return response.data;
}

/**
 * List all registered push devices for the current user
 */
export async function listDevices(): Promise<PushDevice[]> {
  const response = await apiClient.authGet<PushDevicesResponse>('/profile/push-devices');
  return response.data;
}

/**
 * Remove a device registration
 */
export async function removeDevice(deviceId: string): Promise<boolean> {
  await apiClient.authDelete<DeleteDeviceResponse>(`/profile/push-devices/${deviceId}`);
  return true;
}

// -------------------------------------------------------------------
// High-Level API
// -------------------------------------------------------------------

/**
 * Enable push notifications for the current device
 * - Requests permission
 * - Subscribes to push
 * - Registers the device with the backend
 */
export async function enablePushNotifications(
  deviceName?: string
): Promise<{ deviceId: string; subscription: PushSubscription }> {
  // Subscribe to push notifications
  const subscription = await subscribeToPush();
  if (!subscription) {
    throw new Error('Failed to create push subscription');
  }

  // Register the device with the backend
  const device = await registerDevice(subscription, deviceName);

  return {
    deviceId: device.deviceId,
    subscription,
  };
}

/**
 * Disable push notifications for the current device
 * - Finds and removes the device registration
 * - Unsubscribes from push
 */
export async function disablePushNotifications(): Promise<boolean> {
  try {
    // Get current subscription to find the device
    const subscription = await getCurrentSubscription();

    if (subscription) {
      // Find the device by endpoint and remove it
      const devices = await listDevices();
      const currentDevice = devices.find((d) =>
        subscription.endpoint.includes(d.endpointPreview?.replace('...', '') || '')
      );

      if (currentDevice) {
        await removeDevice(currentDevice.deviceId);
      }

      // Unsubscribe from push
      await unsubscribeFromPush();
    }

    return true;
  } catch (error) {
    console.error('Failed to disable push notifications:', error);
    // Still try to unsubscribe even if device removal failed
    await unsubscribeFromPush();
    return false;
  }
}

export const pushService = {
  isPushSupported,
  getPermissionState,
  registerServiceWorker,
  getCurrentSubscription,
  isSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
  registerDevice,
  listDevices,
  removeDevice,
  enablePushNotifications,
  disablePushNotifications,
};

